import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { money } from "@/lib/constants";
import { MapPin } from "lucide-react";

const violetIcon = L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:#8B5CF6;transform:rotate(-45deg);box-shadow:0 0 0 3px rgba(139,92,246,.35)"></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

export default function MapTab({ onOpen }) {
  const { t } = useLang();
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const [studios, setStudios] = useState([]);

  useEffect(() => {
    api.get("/studios").then(({ data }) => setStudios(data.studios));
  }, []);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true }).setView([40.4168, -3.7038], 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || studios.length === 0) return;
    const markers = [];
    const bounds = [];
    studios.forEach((s) => {
      const m = L.marker([s.lat, s.lng], { icon: violetIcon }).addTo(map);
      m.bindPopup(
        `<div style="min-width:160px"><strong>${s.name}</strong><br/><span style="color:#94A3B8">${s.city}</span><br/><span style="color:#8B5CF6;font-weight:700">${money(s.price_per_hour, s.currency)}${t("per_hour")}</span></div>`
      );
      m.on("popupopen", () => {
        const el = document.querySelector(".leaflet-popup-content");
        if (el) el.style.cursor = "pointer";
      });
      m.on("click", () => {});
      m.on("dblclick", () => onOpen(s));
      markers.push(m);
      bounds.push([s.lat, s.lng]);
    });
    if (bounds.length) map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    return () => markers.forEach((m) => map.removeLayer(m));
  }, [studios, onOpen, t]);

  return (
    <div className="ss-rise">
      <style>{`.ss-darkmap .leaflet-tile{filter:invert(1) hue-rotate(180deg) brightness(0.92) contrast(0.9) saturate(0.85);}.ss-darkmap{background:#0b0b12;}`}</style>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">{t("studios_map")}</h1>
      <p className="text-muted-foreground text-sm mb-5 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {t("map_hint")}</p>
      <div
        ref={containerRef}
        data-testid="studios-map"
        className="ss-darkmap w-full h-[60vh] rounded-2xl overflow-hidden border border-border"
      />
      {studios.length === 0 && (
        <p className="text-center text-muted-foreground text-sm mt-4">{t("no_studios")}</p>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
        {studios.map((s) => (
          <button
            key={s.id}
            data-testid={`map-studio-${s.id}`}
            onClick={() => onOpen(s)}
            className="text-left p-4 rounded-xl border border-border bg-card card-hover"
          >
            <p className="font-semibold">{s.name}</p>
            <p className="text-xs text-muted-foreground">{s.city} · {money(s.price_per_hour, s.currency)}{t("per_hour")}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
